import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { sitesApi } from '../api/modules/sites';
import type { UnpublishedSummary, UnpublishedSummaryItem } from '../api/modules/sites';

/** Canonical publish status — matches the backend's per-item status values */
export type PublishStatus = 'never_published' | 'changed' | 'up_to_date';

interface UseUnpublishedSummaryReturn {
  /** Full summary data from the API */
  summary: UnpublishedSummary | null;
  /** Total count of entities with unpublished changes (pages + layouts + blog posts) */
  totalChanged: number;
  /** Whether any entity has unpublished changes */
  hasChanges: boolean;
  /** Whether the summary is currently loading */
  isLoading: boolean;
  /** Error message if the fetch failed */
  error: string | null;
  /** Refetch the summary (e.g. after a publish action) */
  refetch: () => Promise<void>;
  /**
   * After a successful PUT /layouts/:id/publish, force this layout to `up_to_date` locally.
   * Covers replica/refetch lag or a transient summary mismatch so the rocket icon clears immediately.
   */
  markLayoutSynced: (layoutId: string, layoutName?: string) => void;
  /**
   * Lookup maps keyed by entity ID → backend-computed status.
   * Use these for per-item badges to stay consistent with the global count.
   */
  pageStatusMap: Record<string, PublishStatus>;
  layoutStatusMap: Record<string, PublishStatus>;
  blogPostStatusMap: Record<string, PublishStatus>;
}

/**
 * Build an ID → status lookup map from summary items.
 */
function buildStatusMap(items: UnpublishedSummaryItem[]): Record<string, PublishStatus> {
  const map: Record<string, PublishStatus> = {};
  for (const item of items) {
    map[item.id] = item.status;
  }
  return map;
}

/**
 * Hook to fetch and cache the unpublished changes summary for a site.
 * Provides total count, per-entity breakdowns, status lookup maps, and a refetch function.
 */
export function useUnpublishedSummary(siteId: string | undefined): UseUnpublishedSummaryReturn {
  const [summary, setSummary] = useState<UnpublishedSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!siteId) return;

    // Cancel any in-flight request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const data = await sitesApi.getUnpublishedSummary(siteId);
      setSummary(data);
    } catch (err: any) {
      // Ignore abort errors
      if (err?.name === 'AbortError' || err?.name === 'CanceledError') return;
      console.error('Failed to fetch unpublished summary:', err);
      setError(err?.message || 'Failed to fetch unpublished summary');
    } finally {
      setIsLoading(false);
    }
  }, [siteId]);

  // Fetch on mount and when siteId changes
  useEffect(() => {
    fetchSummary();

    return () => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [fetchSummary]);

  const markLayoutSynced = useCallback((layoutId: string, layoutName?: string) => {
    setSummary(prev => {
      if (!prev) return prev;
      const oldItems = prev.summary.layouts.items;
      const hasRow = oldItems.some(i => i.id === layoutId);
      const items: UnpublishedSummaryItem[] = hasRow
        ? oldItems.map(item => (item.id === layoutId ? { ...item, status: 'up_to_date' } : item))
        : [...oldItems, { id: layoutId, name: layoutName, status: 'up_to_date' }];
      const changedLayouts = items.filter(i => i.status !== 'up_to_date').length;
      const changedPages = prev.summary.pages.items.filter(i => i.status !== 'up_to_date').length;
      const changedBlog = prev.summary.blogPosts.items.filter(
        i => i.status !== 'up_to_date'
      ).length;
      return {
        ...prev,
        hasChanges: changedPages + changedLayouts + changedBlog > 0,
        summary: {
          ...prev.summary,
          layouts: {
            ...prev.summary.layouts,
            items,
            changed: changedLayouts,
          },
        },
      };
    });
  }, []);

  const totalChanged = summary
    ? summary.summary.pages.changed +
      summary.summary.layouts.changed +
      summary.summary.blogPosts.changed
    : 0;

  // Build lookup maps (memoised so child components don't re-render unnecessarily)
  const pageStatusMap = useMemo(
    () => (summary ? buildStatusMap(summary.summary.pages.items) : {}),
    [summary]
  );
  const layoutStatusMap = useMemo(
    () => (summary ? buildStatusMap(summary.summary.layouts.items) : {}),
    [summary]
  );
  const blogPostStatusMap = useMemo(
    () => (summary ? buildStatusMap(summary.summary.blogPosts.items) : {}),
    [summary]
  );

  return {
    summary,
    totalChanged,
    hasChanges: totalChanged > 0,
    isLoading,
    error,
    refetch: fetchSummary,
    markLayoutSynced,
    pageStatusMap,
    layoutStatusMap,
    blogPostStatusMap,
  };
}
